import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { HiOutlineFolderPlus, HiOutlinePencilSquare, HiOutlineTrash } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConfirmModal from './ui/ConfirmModal';
import EmptyState from './ui/EmptyState';
import PageHeader from './ui/PageHeader';
import TableSkeleton from './ui/TableSkeleton';

const sortableColumns = [
  { key: 'name', label: 'Name' },
  { key: 'slug', label: 'Slug' },
  { key: 'created_at', label: 'Date' },
];

const BlogCategoryList = () => {
  const baseUrl = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const getBlogCategoryList = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/api/v1/blog-categories`);
      setData(res.data.data || []);
    } catch (err) {
      toast.error('Categories could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBlogCategoryList();
  }, [baseUrl]);

  const sortedData = useMemo(() => {
    const sortedItems = [...data];
    sortedItems.sort((a, b) => {
      const firstValue = a[sortConfig.key] || '';
      const secondValue = b[sortConfig.key] || '';
      const result = String(firstValue).localeCompare(String(secondValue), undefined, {
        numeric: true,
        sensitivity: 'base',
      });

      return sortConfig.direction === 'asc' ? result : -result;
    });

    return sortedItems;
  }, [data, sortConfig]);

  const sortBy = (key) => {
    setSortConfig((currentConfig) => ({
      key,
      direction: currentConfig.key === key && currentConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const deletebtnHandler = async () => {
    if (!pendingDelete) {
      return;
    }

    try {
      setDeleting(true);
      await axios.delete(`${baseUrl}/api/v1/blog-categories/${pendingDelete.id}`);
      toast.success('Category deleted successfully.');
      setPendingDelete(null);
      getBlogCategoryList();
    } catch (err) {
      toast.error('Category could not be deleted.');
    } finally {
      setDeleting(false);
    }
  };

  const renderSortLabel = (column) => {
    const isActive = sortConfig.key === column.key;
    const direction = isActive && sortConfig.direction === 'asc' ? 'up' : 'down';

    return (
      <button className={`table-sort ${isActive ? 'active' : ''}`} type="button" onClick={() => sortBy(column.key)}>
        {column.label}
        <span aria-hidden="true">{direction === 'up' ? '↑' : '↓'}</span>
      </button>
    );
  };

  return (
    <section className="page-stack">
      <PageHeader
        eyebrow="Blog Management"
        title="Category List"
        description="Review, sort, edit, and remove blog categories from one responsive table."
        actions={
          <Link to="/dashboard/blog-category" className="btn btn-primary">
            <HiOutlineFolderPlus aria-hidden="true" />
            Add Category
          </Link>
        }
      />

      <div className="surface-card table-card">
        <div className="table-responsive">
          <table className="table app-table align-middle">
            <thead>
              <tr>
                <th scope="col">S.No.</th>
                <th scope="col">Image</th>
                {sortableColumns.map((column) => (
                  <th scope="col" key={column.key}>
                    {renderSortLabel(column)}
                  </th>
                ))}
                <th scope="col" className="text-end">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton columns={6} rows={6} />
              ) : sortedData.length > 0 ? (
                sortedData.map((item, index) => (
                  <tr key={item.id || item.slug}>
                    <td>{index + 1}</td>
                    <td>
                      <img src={item.image} className="table-image" alt={item.name || 'Category'} />
                    </td>
                    <td className="fw-semibold">{item.name}</td>
                    <td>
                      <span className="slug-pill">{item.slug}</span>
                    </td>
                    <td>{item.created_at}</td>
                    <td>
                      <div className="table-actions justify-content-end">
                        <Link className="icon-button success" to={`/dashboard/blog-category-edit/${item.id}`} aria-label={`Edit ${item.name}`}>
                          <HiOutlinePencilSquare aria-hidden="true" />
                        </Link>
                        <button className="icon-button danger" type="button" aria-label={`Delete ${item.name}`} onClick={() => setPendingDelete(item)}>
                          <HiOutlineTrash aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      title="No categories found"
                      description="Create your first blog category to keep content organized."
                      action={
                        <Link to="/dashboard/blog-category" className="btn btn-primary">
                          Add Category
                        </Link>
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        show={Boolean(pendingDelete)}
        title="Delete category?"
        description={`This will permanently remove ${pendingDelete?.name || 'this category'}.`}
        confirmLabel="Delete Category"
        isBusy={deleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={deletebtnHandler}
      />
    </section>
  );
};

export default BlogCategoryList;
